import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Check, X, RotateCcw, Presentation } from "lucide-react";
import { BoardQuiz } from "@/components/BoardQuiz";
import { useL } from "@/lib/useL";

const API = process.env.REACT_APP_BACKEND_URL;
const LETTERS = "ABCDEF";

const Question = ({ q, i, picked, onPick, done }) => (
  <li className="border border-border bg-card p-5" data-testid={`quiz-question-${i}`}>
    <div className="font-semibold mb-3">{i + 1}. {q.q}</div>
    <div className="grid gap-2">
      {q.options.map((o, j) => {
        const right = done && j === q.answer;
        const wrong = done && j === picked && j !== q.answer;
        const cls = right ? "border-green-600 bg-green-50 dark:bg-green-950" : wrong ? "border-[#FF2A00] bg-red-50 dark:bg-red-950" : picked === j ? "border-[#002FA7] bg-[#002FA7]/10" : "border-border";
        return (
          <button key={j} type="button" disabled={done} onClick={() => onPick(j)} data-testid={`quiz-q${i}-opt-${j}`}
            className={`text-left border-2 px-4 py-3 min-h-[48px] flex items-center gap-3 transition-colors ${cls}`}>
            <span className="font-mono font-bold">{LETTERS[j]}</span>
            <span className="flex-1">{o}</span>
            {right && <Check className="w-4 h-4 text-green-700" />}
            {wrong && <X className="w-4 h-4 text-[#FF2A00]" />}
          </button>
        );
      })}
    </div>
    {done && q.explanation && <p className="text-sm text-muted-foreground mt-3" data-testid={`quiz-q${i}-explanation`}>{q.explanation}</p>}
  </li>
);

// Public pupil quiz: answer every question, then see the score and the corrections. Nothing is stored.
export default function PublicQuiz() {
  const { id } = useParams();
  const L = useL();
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(false);
  const [picks, setPicks] = useState({});
  const [done, setDone] = useState(false);
  const [params, setParams] = useSearchParams();
  const board = params.get("tableau") === "1";

  useEffect(() => {
    fetch(`${API}/api/public/quiz/${id}`).then((r) => (r.ok ? r.json() : Promise.reject())).then(setQuiz).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="quiz-not-found">{L("Ce quiz n'existe pas (ou plus).", "This quiz does not exist (anymore).")}</div>;
  if (!quiz) return <div className="max-w-xl mx-auto px-6 py-20 text-center text-muted-foreground">…</div>;

  if (board) return <BoardQuiz quiz={quiz} onExit={() => setParams({})} />;
  const total = quiz.questions.length;
  const answered = Object.keys(picks).length;
  const score = quiz.questions.filter((q, i) => picks[i] === q.answer).length;
  const restart = () => { setPicks({}); setDone(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="public-quiz-page">
      <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-2">SUX1 · {L("Quiz", "Quiz")}</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ fontFamily: "Outfit" }} data-testid="quiz-title">{quiz.title}</h1>
      <button type="button" onClick={() => setParams({ tableau: "1" })} className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] mb-4 hover:bg-[#002FA7] hover:text-white transition-colors" data-testid="quiz-board-btn"><Presentation className="w-3.5 h-3.5" /> {L("Mode tableau (enseignant)", "Board mode (teacher)")}</button>
      <p className="text-sm text-muted-foreground mb-6">{L("Touchez une réponse par question, puis « Voir ma note ».", "Tap one answer for each question, then « See my score ».")}</p>
      {done && (
        <div className="border-2 border-[#002FA7] p-5 mb-6 text-center" data-testid="quiz-score">
          <div className="text-4xl font-black text-[#002FA7]" style={{ fontFamily: "Outfit" }}>{score} / {total}</div>
          <div className="text-sm mt-1">{score === total ? L("Parfait, bravo !", "Perfect, well done!") : score >= total / 2 ? L("Bien joué ! Relisez les corrections ci-dessous.", "Well done! Read the corrections below.") : L("Relisez les corrections ci-dessous, puis réessayez.", "Read the corrections below, then try again.")}</div>
        </div>
      )}
      <ol className="space-y-4">
        {quiz.questions.map((q, i) => <Question key={i} q={q} i={i} picked={picks[i]} done={done} onPick={(j) => setPicks((p) => ({ ...p, [i]: j }))} />)}
      </ol>
      <div className="sticky bottom-0 bg-background/95 backdrop-blur border-t border-border py-3 mt-6 flex items-center justify-between gap-3">
        <span className="font-mono text-xs text-muted-foreground" data-testid="quiz-progress">{answered} / {total}</span>
        {done ? (
          <button type="button" onClick={restart} className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.16em] text-[11px] px-5 py-3 border border-[#002FA7] text-[#002FA7]" data-testid="quiz-restart"><RotateCcw className="w-3.5 h-3.5" /> {L("Recommencer", "Try again")}</button>
        ) : (
          <button type="button" disabled={answered < total} onClick={() => { setDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="font-mono uppercase tracking-[0.16em] text-[11px] px-5 py-3 bg-[#002FA7] text-white disabled:opacity-40" data-testid="quiz-submit">{L("Voir ma note", "See my score")}</button>
        )}
      </div>
      <a href="/" className="block text-center text-xs text-muted-foreground mt-8 underline" data-testid="quiz-cta">{L("Créer mes propres quiz avec SUX1", "Create my own quizzes with SUX1")}</a>
    </div>
  );
}
